import { QueryFunctionContext, useQueries, useQuery } from '@tanstack/react-query';
import { flatMap, includes, map, max, some, times } from 'es-toolkit/compat';
import { useEffect, useMemo, useState } from 'react';

import {
  ANNOTATIONS_BASE_QUERY_KEY,
  getAllAnnotationAssignments,
  getAnnotationAssignments,
  GetAnnotationAssignmentsParams,
} from 'api/annotationAssignments';
import { NON_SPECIFIC_STUDY_VALUES } from 'components/SearchFilters/hooks/useMainFilters';
import { AnnotationAssignment } from 'interfaces/annotation';
import { Permission } from 'interfaces/permissionOption';
import { StainingMethod } from 'interfaces/stainType';
import pLimit from 'p-limit';
import { CasesParams } from './useCasesParams/types';
import { useCurrentLabId } from './useCurrentLab';
import { usePermissions } from './usePermissions';

export const useAnnotationAssignments = (
  studyId: string,
  withLegacy: boolean = false,
  fetchOnlySlidesOfStudy: boolean = false,
  disabled: boolean = false
) => {
  const { hasPermission } = usePermissions();
  const { labId } = useCurrentLabId();
  const isSpecificStudySelected = Boolean(studyId) && !includes(NON_SPECIFIC_STUDY_VALUES, studyId.toString());
  const canViewAnnotationAssignments = hasPermission(Permission.ViewAnnotationsList) && isSpecificStudySelected;

  const annotationAssignmentsParams = {
    filters: { studyId },
    labId,
    studyId,
    withLegacy,
    fetchOnlySlidesOfStudy,
  };
  const queryKey = [ANNOTATIONS_BASE_QUERY_KEY, annotationAssignmentsParams];

  return {
    ...useQuery({
      queryKey: queryKey,
      queryFn: ({ signal }: QueryFunctionContext) => getAnnotationAssignments(annotationAssignmentsParams, signal),
      enabled: Boolean(!disabled && canViewAnnotationAssignments),
    }),
    queryKey,
  };
};

const limit = pLimit(5); // Limit to 5 concurrent requests - to avoid overwhelming the server or browser
const limitedGetAnnotationAssignments = (params: GetAnnotationAssignmentsParams, signal: AbortSignal) => {
  return limit(() => getAnnotationAssignments(params, signal));
};

export const usePaginatedAnnotationAssignmentParamsQueries = ({
  queryParams,
  pageSize = 20,
  enabled = true,
}: {
  queryParams: GetAnnotationAssignmentsParams;
  pageSize?: number;
  enabled: boolean;
}) => {
  const [pagesToFetch, setPagesToFetch] = useState(1);
  const annotationAssignmentQueries = useQueries({
    queries: times(pagesToFetch, (index) => {
      const paginatedQueryParams = { ...queryParams, page: index, pageSize };

      return {
        queryKey: [ANNOTATIONS_BASE_QUERY_KEY, paginatedQueryParams],
        queryFn: async ({ signal }: QueryFunctionContext) =>
          limitedGetAnnotationAssignments(paginatedQueryParams, signal),
        enabled,
      };
    }),
  });

  const data: AnnotationAssignment[] = useMemo(
    () => flatMap(annotationAssignmentQueries, 'data.annotationAssignments'),
    [annotationAssignmentQueries]
  );
  const isLoading = some(annotationAssignmentQueries, 'isLoading');
  const isError = some(annotationAssignmentQueries, 'isError');

  const totalPages = Math.ceil(
    max([pagesToFetch, ...map(annotationAssignmentQueries, (query) => query.data?.totalAnnotationAssignments ?? 0)]) /
      pageSize
  ); // get the max totalAnnotations from all queries

  useEffect(() => {
    if (totalPages !== pagesToFetch) {
      setPagesToFetch(totalPages);
    }
  }, [pagesToFetch, totalPages]);

  return { data, isLoading, isError, totalPages };
};

export const useAnnotationAssignmentForExecution = ({
  casesParams = {},
  slideStainType,
  stainingMethod,
  skipProcedureParams,
  fetchAll,
  disabled,
}: {
  casesParams?: CasesParams;
  slideStainType?: string;
  stainingMethod?: StainingMethod;
  skipProcedureParams?: boolean;
  fetchAll?: boolean; // if true, fetches all annotation assignments, otherwise fetches only the ones for the current casesParams
  disabled?: boolean;
}) => {
  const queryParams: GetAnnotationAssignmentsParams = {
    ...casesParams,
    slideStainType,
    stainingMethod,
    skipProcedureParams,
    skipSummary: true,
  };
  const { data: specificAnnotationAssignments, isLoading: isSpecificAssignmentIdsLoading } =
    usePaginatedAnnotationAssignmentParamsQueries({ queryParams, enabled: !disabled && !fetchAll });

  const allAnnotationAssignmentsQueryKey = [ANNOTATIONS_BASE_QUERY_KEY, 'all'];
  const { data: allAnnotationAssignments, isLoading: isAllAssignmentIdsLoading } = useQuery({
    queryKey: allAnnotationAssignmentsQueryKey,
    queryFn: ({ signal }: QueryFunctionContext) => getAllAnnotationAssignments({ isGlobal: false, signal }),
    enabled: !disabled && Boolean(fetchAll),
  });

  const annotationAssignments = (fetchAll ? allAnnotationAssignments : specificAnnotationAssignments) || [];
  const isAssignmentIdsLoading = fetchAll ? isAllAssignmentIdsLoading : isSpecificAssignmentIdsLoading;

  return {
    annotationAssignments,
    isAssignmentIdsLoading,
  };
};
